/* ==========================================================================
   Variant picker
   Drives the hidden `id` field from whatever UI the product is configured to
   use (matrix / rows / pills / cards / dropdown), and derives per-day pricing,
   savings and hardware notices from the variant data.
   ========================================================================== */
(function () {
  'use strict';

  var T = window.RaidTheme || {};

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    var fmt = format || T.moneyFormat || '${{amount}}';
    function num(value, precision) {
      if (isNaN(value)) value = 0;
      var parts = (value / 100).toFixed(precision === undefined ? 2 : precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return parts[1] ? whole + '.' + parts[1] : whole;
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return num(cents, 2);
        case 'amount_no_decimals': return num(cents, 0);
        case 'amount_with_comma_separator': return num(cents, 2).replace(/,/g, '§').replace(/\./g, ',').replace(/§/g, '.');
        case 'amount_no_decimals_with_comma_separator': return num(cents, 0).replace(/,/g, '.');
        default: return num(cents, 2);
      }
    });
  }

  /* ---------- duration parsing ----------
     Turns an option value into a day count so we can show per-day pricing.
     Understands "7 Days", "30-day", "1 Month", "Lifetime", "365". Returns
     null when a value clearly isn't a duration, which disables the maths. */
  function parseDays(value) {
    if (!value) return null;
    var s = String(value).toLowerCase().trim();

    if (/life ?time|permanent|forever|unlimited/.test(s)) return null;

    // NB: plurals matter — "7 Days" must match, so allow a trailing s
    var m = s.match(/(\d+(?:\.\d+)?)\s*(?:days?|d)\b/);
    if (m) return parseFloat(m[1]);

    m = s.match(/(\d+(?:\.\d+)?)\s*(?:weeks?|wks?|w)\b/);
    if (m) return parseFloat(m[1]) * 7;

    m = s.match(/(\d+(?:\.\d+)?)\s*(?:months?|mos?|m)\b/);
    if (m) return parseFloat(m[1]) * 30;

    m = s.match(/(\d+(?:\.\d+)?)\s*(?:years?|yrs?|y)\b/);
    if (m) return parseFloat(m[1]) * 365;

    // bare number, e.g. an option literally called "30"
    m = s.match(/^(\d+(?:\.\d+)?)$/);
    if (m) return parseFloat(m[1]);

    return null;
  }

  function VariantPicker(root) {
    this.root = root;
    this.form = document.getElementById(root.dataset.formId) ||
                document.querySelector('[id="' + root.dataset.formId + '"]');
    this.hidden = root.querySelector('[data-selected-variant]');

    var dataEl = root.querySelector('[data-variant-data]');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[variant-picker] could not parse variant data', e);
      return;
    }

    this.variants = this.data.variants || [];
    this.optionNames = this.data.optionNames || [];
    this.hardware = this.data.hardware || {};
    this.layout = root.dataset.layout;
    this.showPerDay = root.dataset.showPerDay === 'true';
    this.showSavings = root.dataset.showSavings === 'true';
    this.baseline = root.dataset.savingsBaseline || 'shortest';
    this.hideUnavailable = root.dataset.hideUnavailable === 'true';

    this.selected = this.currentVariant() || this.firstAvailable();
    this.bind();
    this.update();
  }

  VariantPicker.prototype.firstAvailable = function () {
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].available) return this.variants[i];
    }
    return this.variants[0] || null;
  };

  VariantPicker.prototype.currentVariant = function () {
    var id = this.hidden && parseInt(this.hidden.value, 10);
    if (!id) return null;
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].id === id) return this.variants[i];
    }
    return null;
  };

  /* Which option index carries a duration, if any. */
  VariantPicker.prototype.durationIndex = function () {
    if (this._durIdx !== undefined) return this._durIdx;
    this._durIdx = -1;
    for (var i = 0; i < this.optionNames.length; i++) {
      var withDays = 0, total = 0;
      for (var v = 0; v < this.variants.length; v++) {
        var val = this.variants[v].options[i];
        if (val === undefined) continue;
        total++;
        if (parseDays(val) !== null) withDays++;
      }
      // treat as a duration axis only if most values parse
      if (total > 0 && withDays / total >= 0.6) { this._durIdx = i; break; }
    }
    return this._durIdx;
  };

  VariantPicker.prototype.bind = function () {
    var self = this;

    // radio inputs (pills / rows / cards)
    this.root.querySelectorAll('[data-option-input]').forEach(function (input) {
      input.addEventListener('change', function () { self.onOptionChange(); });
    });

    // dropdowns
    this.root.querySelectorAll('[data-option-select]').forEach(function (sel) {
      sel.addEventListener('change', function () { self.onOptionChange(); });
    });

    // matrix cells
    this.root.querySelectorAll('[data-cell]').forEach(function (cell) {
      var btn = cell.querySelector('button');
      if (!btn || btn.disabled) return;
      btn.addEventListener('click', function () {
        var id = parseInt(cell.dataset.variantId, 10);
        var found = self.variants.filter(function (v) { return v.id === id; })[0];
        if (found) { self.selected = found; self.update(); }
      });
    });
  };

  VariantPicker.prototype.readOptions = function () {
    var values = [];
    var self = this;

    this.root.querySelectorAll('[data-option-group]').forEach(function (group) {
      var pos = parseInt(group.dataset.optionIndex, 10) - 1;
      var checked = group.querySelector('[data-option-input]:checked');
      var select = group.querySelector('[data-option-select]');
      if (checked) values[pos] = checked.value;
      else if (select) values[pos] = select.value;
    });

    // fall back to the current selection for anything unread
    if (self.selected) {
      for (var i = 0; i < self.optionNames.length; i++) {
        if (values[i] === undefined) values[i] = self.selected.options[i];
      }
    }
    return values;
  };

  VariantPicker.prototype.onOptionChange = function () {
    var wanted = this.readOptions();
    var match = null;

    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var j = 0; j < wanted.length; j++) {
        if (wanted[j] !== undefined && v.options[j] !== wanted[j]) { ok = false; break; }
      }
      if (ok) { match = v; break; }
    }

    // If the combination doesn't exist, keep the first option and pick the
    // first available value on the remaining axes rather than dead-ending.
    if (!match) {
      for (var k = 0; k < this.variants.length; k++) {
        if (this.variants[k].options[0] === wanted[0] && this.variants[k].available) {
          match = this.variants[k];
          break;
        }
      }
    }

    if (match) { this.selected = match; this.update(); }
  };

  VariantPicker.prototype.update = function () {
    if (!this.selected) return;

    if (this.hidden) this.hidden.value = this.selected.id;
    this.syncInputs();
    this.syncMatrix();
    this.syncPricing();
    this.syncHardware();
    this.syncUpgrade();
    this.syncAddButton();
    this.syncMedia();
    this.pushUrl();

    this.root.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant: this.selected }
    }));
  };

  VariantPicker.prototype.syncInputs = function () {
    var self = this;
    this.root.querySelectorAll('[data-option-group]').forEach(function (group) {
      var pos = parseInt(group.dataset.optionIndex, 10) - 1;
      var want = self.selected.options[pos];

      group.querySelectorAll('[data-option-input]').forEach(function (input) {
        input.checked = input.value === want;

        // disable combinations that don't exist with the current other options
        var probe = self.selected.options.slice();
        probe[pos] = input.value;
        var exists = self.variants.some(function (v) {
          for (var i = 0; i < probe.length; i++) {
            if (i !== pos && v.options[i] !== probe[i]) return false;
          }
          return v.options[pos] === input.value && v.available;
        });

        input.disabled = !exists;
        var wrap = input.closest('[data-value-wrap]');
        if (wrap && self.hideUnavailable) wrap.classList.toggle('pill-opt--hidden', !exists);
      });

      var select = group.querySelector('[data-option-select]');
      if (select) select.value = want;
    });
  };

  VariantPicker.prototype.syncMatrix = function () {
    var self = this;
    this.root.querySelectorAll('[data-cell]').forEach(function (cell) {
      cell.classList.toggle('is-selected', parseInt(cell.dataset.variantId, 10) === self.selected.id);
    });
  };

  /* Per-day price, savings badges, "from" hints. */
  VariantPicker.prototype.syncPricing = function () {
    var self = this;
    var durIdx = this.durationIndex();

    // --- per-value price + per-day on rows/cards ---
    this.root.querySelectorAll('[data-option-group]').forEach(function (group) {
      var pos = parseInt(group.dataset.optionIndex, 10) - 1;

      group.querySelectorAll('[data-value-wrap]').forEach(function (wrap) {
        var input = wrap.querySelector('[data-option-input]');
        if (!input) return;

        var probe = self.selected.options.slice();
        probe[pos] = input.value;
        var v = self.variants.filter(function (cand) {
          for (var i = 0; i < probe.length; i++) {
            if (cand.options[i] !== probe[i]) return false;
          }
          return true;
        })[0];
        if (!v) return;

        var priceEl = wrap.querySelector('[data-value-price]');
        if (priceEl) priceEl.textContent = formatMoney(v.price);

        var fromEl = wrap.querySelector('[data-value-from]');
        if (fromEl) {
          var cheapest = null;
          self.variants.forEach(function (cand) {
            if (cand.options[pos] !== input.value || !cand.available) return;
            if (cheapest === null || cand.price < cheapest) cheapest = cand.price;
          });
          if (cheapest !== null) fromEl.textContent = 'from ' + formatMoney(cheapest);
        }

        var perEl = wrap.querySelector('[data-per-day]');
        if (perEl && self.showPerDay && durIdx === pos) {
          var days = parseDays(input.value);
          perEl.textContent = days ? formatMoney(Math.round(v.price / days)) + ' / day' : '';
        }

        var saveSlot = wrap.querySelector('[data-save-slot]');
        if (saveSlot) {
          saveSlot.innerHTML = '';
          if (self.showSavings && durIdx === pos) {
            var pct = self.savingsFor(v, pos);
            if (pct >= 5) {
              var b = document.createElement('span');
              b.className = 'badge-save';
              b.textContent = '−' + pct + '%';
              saveSlot.appendChild(b);
            }
          }
        }

        // savings shown inline on pills too, since pills have no dedicated slot
        var pillFace = wrap.querySelector('.pill-opt__face small[data-value-from]');
        if (pillFace && self.showSavings && durIdx === pos) {
          var p2 = self.savingsFor(v, pos);
          pillFace.textContent = p2 >= 5
            ? formatMoney(v.price) + '  −' + p2 + '%'
            : formatMoney(v.price);
        }
      });
    });

    // --- big price + selection summary ---
    var priceTarget = document.querySelector('[data-price-target]');
    if (priceTarget) priceTarget.textContent = formatMoney(this.selected.price);

    var compareTarget = document.querySelector('[data-compare-target]');
    if (compareTarget) {
      if (this.selected.compare_at_price > this.selected.price) {
        compareTarget.textContent = formatMoney(this.selected.compare_at_price);
        compareTarget.hidden = false;
      } else {
        compareTarget.hidden = true;
      }
    }

    var nameTarget = document.querySelector('[data-selection-target]');
    if (nameTarget) nameTarget.textContent = this.selected.title;

    var perTarget = document.querySelector('[data-perday-target]');
    if (perTarget) {
      var d = durIdx >= 0 ? parseDays(this.selected.options[durIdx]) : null;
      perTarget.textContent = (this.showPerDay && d)
        ? formatMoney(Math.round(this.selected.price / d)) + ' per day'
        : '';
    }

    // sticky mobile bar
    var stickyName = document.querySelector('[data-sticky-name]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    if (stickyName) stickyName.textContent = this.selected.title;
    if (stickyPrice) stickyPrice.textContent = formatMoney(this.selected.price);
  };

  /* Savings vs the baseline term, as a whole percentage. */
  VariantPicker.prototype.savingsFor = function (variant, durPos) {
    var days = parseDays(variant.options[durPos]);
    if (!days) return 0;

    var siblings = [];
    var self = this;
    this.variants.forEach(function (v) {
      for (var i = 0; i < v.options.length; i++) {
        if (i !== durPos && v.options[i] !== variant.options[i]) return;
      }
      var d = parseDays(v.options[durPos]);
      if (d) siblings.push({ days: d, price: v.price });
    });
    if (siblings.length < 2) return 0;

    siblings.sort(function (a, b) { return a.days - b.days; });
    var base = this.baseline === 'second' && siblings.length > 1 ? siblings[1] : siblings[0];
    var baseRate = base.price / base.days;
    if (!baseRate) return 0;

    var rate = variant.price / days;
    return Math.round((1 - rate / baseRate) * 100);
  };

  /* Hardware notice — driven by the per-value metafield map. */
  VariantPicker.prototype.syncHardware = function () {
    var notice = document.querySelector('[data-hardware-notice]');
    if (!notice) return;

    var needs = false;
    var label = '';
    for (var i = 0; i < this.selected.options.length; i++) {
      var val = this.selected.options[i];
      if (Object.prototype.hasOwnProperty.call(this.hardware, val)) {
        if (this.hardware[val]) { needs = true; label = val; }
        else if (!label) { label = val; }
      }
    }

    var titleEl = notice.querySelector('[data-hardware-title]');
    var bodyEl = notice.querySelector('[data-hardware-body]');
    var iconEl = notice.querySelector('[data-hardware-icon]');

    notice.classList.toggle('notice--ok', !needs);
    if (iconEl) iconEl.textContent = needs ? '!' : '✓';

    var reqTitle = notice.dataset.titleRequired || 'Hardware required';
    var okTitle = notice.dataset.titleOptional || 'No extra hardware needed';
    var reqBody = notice.dataset.bodyRequired || '';
    var okBody = notice.dataset.bodyOptional || '';

    if (titleEl) titleEl.textContent = needs ? reqTitle : okTitle;
    if (bodyEl) bodyEl.innerHTML = (needs ? reqBody : okBody).replace(/\[option\]/g, label);

    notice.hidden = Object.keys(this.hardware).length === 0;
  };

  /* Upgrade nudge — when a short term is selected, show what the next tier up
     costs per day. 62% of orders are the shortest term, so this is where the
     money is. Only fires when the longer option is genuinely better value. */
  VariantPicker.prototype.syncUpgrade = function () {
    var box = document.querySelector('[data-upgrade-nudge]');
    if (!box) return;

    var durPos = this.durationIndex();
    if (durPos < 0) { box.hidden = true; return; }

    var days = parseDays(this.selected.options[durPos]);
    if (!days) { box.hidden = true; return; }

    // siblings on the duration axis only
    var self = this;
    var siblings = this.variants.filter(function (v) {
      for (var i = 0; i < v.options.length; i++) {
        if (i !== durPos && v.options[i] !== self.selected.options[i]) return false;
      }
      return v.available && parseDays(v.options[durPos]);
    });

    var current = { days: days, price: this.selected.price };
    var currentRate = current.price / current.days;

    // the next longer term up from here
    var next = null;
    siblings.forEach(function (v) {
      var d = parseDays(v.options[durPos]);
      if (d <= current.days) return;
      if (!next || d < parseDays(next.options[durPos])) next = v;
    });

    if (!next) { box.hidden = true; return; }

    var nextDays = parseDays(next.options[durPos]);
    var nextRate = next.price / nextDays;
    var saving = Math.round((1 - nextRate / currentRate) * 100);

    if (saving < 10) { box.hidden = true; return; }

    var label = box.querySelector('[data-upgrade-text]');
    var action = box.querySelector('[data-upgrade-action]');
    var tpl = box.dataset.template ||
      'Switch to [term] and pay [saving]% less per day.';

    if (label) {
      label.innerHTML = tpl
        .replace(/\[term\]/g, '<strong>' + next.options[durPos] + '</strong>')
        .replace(/\[saving\]/g, '<strong>' + saving + '%</strong>')
        .replace(/\[price\]/g, formatMoney(next.price))
        .replace(/\[perday\]/g, formatMoney(Math.round(nextRate)));
    }

    if (action) {
      action.textContent = (box.dataset.actionText || 'Switch to [term]')
        .replace(/\[term\]/g, next.options[durPos]);
      action.onclick = function () {
        self.selected = next;
        self.renderAll();
      };
    }

    box.hidden = false;
  };

  /* re-render option UI then sync — used when JS changes the selection */
  VariantPicker.prototype.renderAll = function () {
    this.update();
  };

  VariantPicker.prototype.syncAddButton = function () {
    var btn = document.querySelector('[data-add-button]');
    if (!btn) return;
    var label = btn.querySelector('[data-add-label]') || btn;

    if (!this.selected) {
      btn.disabled = true;
      label.textContent = T.strings.unavailable;
    } else if (!this.selected.available) {
      btn.disabled = true;
      label.textContent = T.strings.soldOut;
    } else {
      btn.disabled = false;
      label.textContent = T.strings.addToCart;
    }
  };

  VariantPicker.prototype.syncMedia = function () {
    if (!this.selected.featured_media) return;
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;

    var target = gallery.querySelector('[data-media-id="' + this.selected.featured_media + '"]');
    if (!target) return;

    gallery.querySelectorAll('[data-media-id]').forEach(function (el) {
      el.classList.toggle('is-active', el === target);
    });
    gallery.querySelectorAll('[data-thumb]').forEach(function (t) {
      t.setAttribute('aria-current', t.dataset.thumb === String(this.selected.featured_media));
    }, this);
  };

  VariantPicker.prototype.pushUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', this.selected.id);
    window.history.replaceState({}, '', url.toString());
  };

  function init() {
    document.querySelectorAll('[data-variant-picker]').forEach(function (el) {
      if (!el.__picker) el.__picker = new VariantPicker(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // re-init when the theme editor swaps a section
  document.addEventListener('shopify:section:load', init);

  window.RaidVariantPicker = VariantPicker;
})();
